import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'

export type NotificationType = 'success' | 'error' | 'warning' | 'primary'
export type NotificationPosition = 'top' | 'bottom' | 'top-right' | 'bottom-right'

export interface AlertNotification {
  id: string
  type: NotificationType
  message: string
  position?: 'top' | 'bottom'
  duration?: number
  onConfirm?: () => void
  onCancel?: () => void
  confirmText?: string
  cancelText?: string
  confirmButtonVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
  cancelButtonVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
}

export interface ToastNotification {
  id: string
  type: NotificationType
  title: string
  message: string
  position?: 'top-right' | 'bottom-right'
  duration?: number
}

export type Notification = AlertNotification | ToastNotification

interface NotificationContextProps {
  alerts: AlertNotification[]
  toasts: ToastNotification[]
  showAlert: (alert: Omit<AlertNotification, 'id'>) => void
  showToast: (toast: Omit<ToastNotification, 'id'>) => void
  removeAlert: (id: string) => void
  removeToast: (id: string) => void
}

const NotificationContext = createContext<NotificationContextProps | undefined>(undefined)

let idCounter = 0

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [alerts, setAlerts] = useState<AlertNotification[]>([])
  const [toasts, setToasts] = useState<ToastNotification[]>([])
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())

  const removeAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showAlert = useCallback((alert: Omit<AlertNotification, 'id'>) => {
    const id = `alert-${idCounter++}`
    setAlerts((prev) => [...prev, { ...alert, id }])
    const duration = alert.duration ?? 3000
    const timer = setTimeout(() => {
      timers.current.delete(timer)
      removeAlert(id)
    }, duration)
    timers.current.add(timer)
  }, [removeAlert])

  const showToast = useCallback((toast: Omit<ToastNotification, 'id'>) => {
    const id = `toast-${idCounter++}`
    setToasts((prev) => [...prev, { ...toast, id }])
    if (toast.duration) {
      const timer = setTimeout(() => {
        timers.current.delete(timer)
        removeToast(id)
      }, toast.duration)
      timers.current.add(timer)
    }
  }, [removeToast])

  useEffect(() => () => {
    timers.current.forEach(clearTimeout)
    timers.current.clear()
  }, [])

  const value = useMemo(() => ({
    alerts,
    toasts,
    showAlert,
    showToast,
    removeAlert,
    removeToast,
  }), [alerts, toasts, showAlert, showToast, removeAlert, removeToast])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotification должен использоваться внутри NotificationProvider')
  return ctx
} 
